import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { ExternalLink } from 'lucide-react'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'

import { ExecutionMethodBadge, TaskStatusBadge, WorkflowStatusBadge } from 'components/brokers/workflows/WorkflowVisualIndicators'
import { useJobsViewNavigation } from 'components/context/JobsViewContext'
import { type TasksFilters, useGetTasks } from 'components/hooks/tasks/useGetTasks'
import DisplayTooltipOnHover from 'components/reusable/DisplayTooltipOnHover'
import { SortableHeader } from 'components/reusable/table/SortableHeader'
import TanstackTable from 'components/reusable/table/TanstackTable'
import { Checkbox } from 'components/ui/checkbox'
import { getLocaleDateString } from 'utils'
import { type BrokersTask } from 'utils/web_api.schemas'

import AssignBrokersToTask from './AssignBrokersToTask'
import { createAddArrayFilter, getCarrierFilterInfo } from './utils'
import { NextUpTasksList } from '../workflows/NextUpTasksList'

type ColumnVisibilityOverrideOption = 'openInNewTab'

type TasksTableProps = {
  filters: TasksFilters
  setFilters: Dispatch<SetStateAction<TasksFilters>>
  pagination: PaginationState
  setPagination: Dispatch<SetStateAction<PaginationState>>
  sorting: SortingState
  setSorting: Dispatch<SetStateAction<SortingState>>
  columnVisibilityOverrides?: Partial<
    Record<keyof BrokersTask | 'job_type' | 'job_handler' | 'assignees' | 'actions' | 'prerequisite_tasks' | 'dependent_tasks' | 'coverage_start_date', boolean>
  >
  setColumnVisibilityOverrides?: Dispatch<SetStateAction<Partial<
    Record<keyof BrokersTask | 'job_type' | 'job_handler' | 'assignees' | 'actions' | 'prerequisite_tasks' | 'dependent_tasks' | 'coverage_start_date', boolean>
  >>>
  actionsVisibilityOverrides?: Record<ColumnVisibilityOverrideOption, boolean>
  rowSelection?: RowSelectionState
  setRowSelection?: Dispatch<SetStateAction<RowSelectionState>>
}

const columnHelper = createColumnHelper<BrokersTask>()

const TasksTable = ({
  filters,
  setFilters,
  pagination,
  setPagination,
  sorting,
  setSorting,
  actionsVisibilityOverrides = { openInNewTab: true },
  columnVisibilityOverrides,
  setColumnVisibilityOverrides,
  rowSelection = {},
  setRowSelection = () => {},
}: TasksTableProps) => {
  const { data, isLoading, isError } = useGetTasks({
    filters,
    pagination,
    sorting,
  })

  const [paginationMetadata, setPaginationMetadata] = useState<{
    total_records: number
    current_page: number
    total_pages: number
    next_page: number | null
    prev_page: number | null
  }>()

  // Helper function to add a value to an array filter
  const addArrayFilter = useMemo(() => createAddArrayFilter(setFilters), [setFilters])

  // Memoize the columns to avoid re-rendering the table.
  const columns = useMemo(() => {
    return [
      // Selection column
      columnHelper.display({
        id: 'select',
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllPageRowsSelected() ||
              (table.getIsSomePageRowsSelected() && 'indeterminate')
            }
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)}
            aria-label='Select all'
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label='Select row'
          />
        ),
        enableSorting: false,
        enableHiding: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }: { row: Row<BrokersTask> }) => {
          const { navigateToTask } = useJobsViewNavigation()

          return (
            <div className='flex items-center gap-4'>
              {actionsVisibilityOverrides?.openInNewTab && row.original.job && (
                <DisplayTooltipOnHover text='Open task'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.preventDefault()
                      navigateToTask(row.original.job?.handler_name ?? '', row.original.id)
                    }}
                  >
                    <ExternalLink
                      size={20}
                      className='stroke-current text-gray-600'
                    />
                  </div>
                </DisplayTooltipOnHover>
              )}

              {actionsVisibilityOverrides?.openInNewTab && row.original.workflow_id && (
                <DisplayTooltipOnHover text='Open task'>
                  <div
                    className='cursor-pointer h-full'
                    onClick={(e) => {
                      e.preventDefault()
                      window.open(`/brokers/workflows/${row.original.workflow_id}`, '_blank')
                    }}
                  >
                    <ExternalLink
                      size={20}
                      className='stroke-current text-gray-600'
                    />
                  </div>
                </DisplayTooltipOnHover>
              )}

            </div>
          )
        },
      }),
      columnHelper.accessor('created_at', {
        header: (info) => (
          <SortableHeader<BrokersTask> {...info} headerText='Date' />
        ),
        cell: (info) => getLocaleDateString(new Date(info.getValue())),
        enableSorting: true,
      }),
      columnHelper.accessor('member_name', {
        header: 'Member',
        cell: (info) => {
          const memberName = info.getValue()
          const memberId = info.row.original.member_id

          if (!memberName) return '-'

          return (
            <span
              className='cursor-pointer hover:underline'
              onClick={(e) => {
                e.stopPropagation()
                if (memberId) {
                  addArrayFilter('member_ids', memberId)
                }
              }}
            >
              {memberName}
            </span>
          )
        },
      }),
      columnHelper.accessor('carrier_name', {
        header: 'Carrier',
        cell: (info) => {
          const carrierName = info.getValue()
          if (!carrierName) return '-'

          return (
            <span
              className='cursor-pointer hover:underline'
              onClick={(e) => {
                e.stopPropagation()
                const { key, value } = getCarrierFilterInfo(carrierName)
                addArrayFilter(key, value)
              }}
            >
              {carrierName}
            </span>
          )
        },
      }),
      columnHelper.accessor('coverage_start_date', {
        header: 'Coverage Start',
        cell: (info) => {
          const date = info.getValue()
          if (!date) return '-'
          return getLocaleDateString(new Date(date))
        },
      }),
      columnHelper.accessor('brokers', {
        id: 'assignees',
        header: 'Assignees',
        cell: ({ getValue, row }) => {
          const { id } = row.original
          return (
            <AssignBrokersToTask
              id={id}
              assignedBrokers={getValue() || []}
            />
          )
        },
      }),
      columnHelper.accessor('status', {
        header: (info) => (
          <SortableHeader<BrokersTask> {...info} headerText='Status' />
        ),
        cell: (info) => {
          const status = info.getValue()
          return (
            <TaskStatusBadge
              status={status}
              isExecutable={info.row.original.can_execute}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                addArrayFilter('statuses', status)
              }}
            />
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('execution_method', {
        header: (info) => (
          <SortableHeader<BrokersTask>
            {...info}
            headerText='Execution Method'
          />
        ),
        cell: (info) => {
          const method = info.getValue()
          return (
            <ExecutionMethodBadge
              method={method}
              onClick={
                method
                  ? (e: React.MouseEvent) => {
                      e.stopPropagation()
                      addArrayFilter('execution_methods', method)
                    }
                  : undefined
              }
            />
          )
        },
        enableSorting: true,
      }),
      columnHelper.accessor('workflow_status', {
        header: 'Workflow Status',
        cell: (info) => {
          const status = info.getValue()

          if (!status) return '-'

          return <WorkflowStatusBadge status={status} />
        },
      }),
      columnHelper.accessor('display_name', {
        id: 'task_type',
        header: 'Task Type',
        cell: (info) => {
          const displayName = info.getValue()
          const taskType = info.row.original.task_type

          if (!displayName) return '-'

          return (
            <span
              className='cursor-pointer hover:underline'
              onClick={(e) => {
                e.stopPropagation()
                if (taskType) {
                  addArrayFilter('task_types', taskType)
                }
              }}
            >
              {displayName}
            </span>
          )
        },
      }),
      columnHelper.accessor('job.type', {
        id: 'job_type',
        header: 'Job Type',
      }),
      columnHelper.accessor('job.description', {
        id: 'job_handler',
        header: 'Handler',
      }),
      columnHelper.accessor('message', {
        id: 'message',
        header: 'Message',
        cell: (info) => <div className='text-sm max-w-[400px] truncate'>{info.getValue()}</div>,
      }),
      columnHelper.accessor('prerequisite_tasks_summary', {
        id: 'prerequisite_tasks',
        header: 'Dependent Task(s)',
        cell: (info) => {
          const tasks = info.getValue()
          return <NextUpTasksList tasks={tasks ?? []} maxVisible={2} />
        },
      }),
      columnHelper.accessor('dependent_tasks_summary', {
        id: 'dependent_tasks',
        header: 'Next Task(s)',
        cell: (info) => {
          const tasks = info.getValue()
          return <NextUpTasksList tasks={tasks ?? []} maxVisible={2} />
        },
      }),
    ]
  }, [actionsVisibilityOverrides, addArrayFilter])

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    state: {
      pagination,
      sorting,
      columnVisibility: columnVisibilityOverrides,
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibilityOverrides,
    enableRowSelection: true,
    enableHiding: true,
    getRowId: (row) => row.id,
    manualPagination: true,
    manualSorting: true,
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no tasks that need attention.'
      setSorting={setSorting}
      defaultSortingState={[]}
      paginationLabelSingular='task'
      paginationMetadata={paginationMetadata}
      tableMessageHeightClass='h-32'
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

export default TasksTable
